import {
  DmoMap,
  DMOMapPartnershipInviteCreatePayload,
  DMOMapPartnershipInviteDetails,
  DMOMapPartnershipInviteListItem,
} from '@/models/dmoMap.modal'
import { useAuthStore } from '@/store/auth'
import { useFilterStore } from '@/store/filter'
import { getOptionKeys, getOrdering } from '@/utils/general'
import axios from 'axios'
import { storeToRefs } from 'pinia'
import { Ref } from 'vue'
import { useMutation, useQuery } from 'vue-query'

export const mapKeys = {
  all: ['maps'] as const,
  lists: () => [...mapKeys.all, 'list'] as const,
  list: (
    companyId: Ref<number | null>,
    pagination: Ref<{ page: number; page_size: number }>,
    requestSwitch: Ref<boolean>,
    owned: Ref<boolean>,
  ) =>
    [
      ...mapKeys.lists(),
      { companyId, pagination, requestSwitch, owned },
    ] as const,
}

export const availableServiceKeys = {
  all: ['available-services'] as const,
  lists: () => [...availableServiceKeys.all, 'list'] as const,
  list: (
    companyId: Ref<number | null>,
    mapId: number,
    pagination: Ref<{ page: number; page_size: number }>,
    requestSwitch: Ref<boolean>,
  ) =>
    [
      ...availableServiceKeys.lists(),
      { companyId, mapId, pagination, requestSwitch },
    ] as const,
}

interface MapResponse {
  id: number
  name: string
  description: string
  created_at: string
  updated_at: string
  status: string
}

interface ServiceResponse {
  id: number
  name: string
  type: string
  description?: string
  is_private: boolean
  published_at?: string
  archived_at?: string
  created_at: string
  updated_at: string
  labels?: string[]
}

interface PaginatedResponse<T> {
  results: T[]
  count: number
}

export function useMapListQuery(
  pagination: Ref<{ page: number; page_size: number }>,
  requestSwitch: Ref<boolean>,
  owned: Ref<boolean>,
) {
  const authStore = useAuthStore()
  const { selectedCompanyId } = storeToRefs(authStore)

  return useQuery(
    mapKeys.list(selectedCompanyId, pagination, requestSwitch, owned),
    async () => {
      const { data } = await axios.get<PaginatedResponse<MapResponse>>(
        `/companies/${selectedCompanyId.value}/maps/`,
        {
          params: {
            page: pagination.value.page,
            page_size: pagination.value.page_size,
            owned: owned.value,
          },
        },
      )
      return data
    },
    {
      enabled: requestSwitch.value,
      keepPreviousData: true,
    },
  )
}

export function useAvailableServicesQuery(
  mapId: number,
  pagination: Ref<{ page: number; page_size: number }>,
  requestSwitch: Ref<boolean>,
) {
  const authStore = useAuthStore()
  const { selectedCompanyId } = storeToRefs(authStore)
  const { serviceFilterObj } = useFilterStore()

  return useQuery(
    availableServiceKeys.list(
      selectedCompanyId,
      mapId,
      pagination,
      requestSwitch,
    ),
    async () => {
      const params = {
        page: pagination.value.page,
        page_size: pagination.value.page_size,
        labels:
          serviceFilterObj.filters.labels.length !== 0
            ? String(getOptionKeys(serviceFilterObj.filters.labels))
            : null,
        search: serviceFilterObj.filters.search
          ? serviceFilterObj.filters.search
          : null,
        ordering: getOrdering(serviceFilterObj.ordering),
        type: serviceFilterObj.filters.type?.key,
      }

      const { data } = await axios.get<PaginatedResponse<ServiceResponse>>(
        `/companies/${selectedCompanyId.value}/maps/${mapId}/filter-services/`,
        { params },
      )
      return data
    },
    {
      enabled: requestSwitch.value,
      keepPreviousData: true,
    },
  )
}

export function useCreateMapMutation(selectedCompanyId: Ref<number | null>) {
  return useMutation(async (mapData: DmoMap) => {
    const { data } = await axios.post(
      `companies/${selectedCompanyId.value}/maps/`,
      mapData,
    )

    return data
  })
}

export function useUpdateMapMutation(selectedCompanyId: Ref<number | null>) {
  return useMutation(async (mapData: DmoMap) => {
    const { data } = await axios.put(
      `companies/${selectedCompanyId.value}/maps/${mapData.id}/`,
      mapData,
    )

    return data
  })
}

export function useGetMapMutation(selectedCompanyId: Ref<number | null>) {
  return useMutation(async (mapId: number) => {
    const { data } = await axios.get(
      `companies/${selectedCompanyId.value}/maps/${mapId}/`,
    )

    return data
  })
}

export function useDeleteMapMutation(selectedCompanyId: Ref<number | null>) {
  return useMutation(async (mapId: number) => {
    const { data } = await axios.delete(
      `companies/${selectedCompanyId.value}/maps/${mapId}/`,
    )

    return data
  })
}

export function useGetCanCreateMapsMutation(
  selectedCompanyId: Ref<number | null>,
) {
  return useMutation(async (mapId: number) => {
    const { data } = await axios.get(
      `companies/${selectedCompanyId.value}/maps/can-create-maps/`,
    )

    return data
  })
}

export function useCreateMapOrderMappingMutation(
  selectedCompanyId: Ref<number | null>,
) {
  return useMutation(async (body: { map_id: number; order_id: number }) => {
    const { data } = await axios.post(
      `companies/${selectedCompanyId.value}/maps/map-order-mapping/`,
      body,
    )
    return data
  })
}

export function useGetMapServices(
  selectedCompanyId: Ref<number | null>,
  mapId: number,
) {
  return useMutation(async () => {
    const { data } = await axios.get(
      `/companies/${selectedCompanyId.value}/maps/${mapId}/services/`,
    )

    return data
  })
}

export function useRemoveMapService(
  selectedCompanyId: Ref<number | null>,
  mapId: number,
) {
  return useMutation(async (serviceId: number) => {
    const { data } = await axios.post(
      `/companies/${selectedCompanyId.value}/maps/${mapId}/remove-services/`,
      {
        service_ids: [serviceId],
      },
    )

    return data
  })
}

export function useAddMapServices(
  selectedCompanyId: Ref<number | null>,
  mapId: number,
) {
  return useMutation(async (serviceIds: number[]) => {
    const { data } = await axios.post(
      `/companies/${selectedCompanyId.value}/maps/${mapId}/add-services/`,
      {
        service_ids: serviceIds,
      },
    )

    return data
  })
}

export function useGetCompanies(
  selectedCompanyId: Ref<number | null>,
  mapId: number,
) {
  return useMutation(async () => {
    const { data } = await axios.get(
      `/companies/${selectedCompanyId.value}/maps/${mapId}/companies/`,
    )
    return data
  })
}

export function useRemoveCompany(
  selectedCompanyId: Ref<number | null>,
  mapId: number,
) {
  return useMutation(async (companyId: number) => {
    const { data } = await axios.post(
      `/companies/${selectedCompanyId.value}/maps/${mapId}/companies/${companyId}/remove/`,
    )
    return data
  })
}

export function useGetAllMaps() {
  return useMutation(async () => {
    const { data } = await axios.get(`/public/dmo-maps/`)
    return data
  })
}

export function useInviteProviderMutation(
  selectedCompanyId: Ref<number | null>,
  mapId: number,
) {
  return useMutation(async (payload: DMOMapPartnershipInviteCreatePayload) => {
    const { data } = await axios.post<DMOMapPartnershipInviteDetails>(
      `companies/${selectedCompanyId.value}/maps/${mapId}/invites/`,
      payload,
    )
    return data
  })
}

export function useGetDmoMapInvites(
  selectedCompanyId: Ref<number | null>,
  mapId: number,
) {
  return useMutation(async () => {
    const { data } = await axios.get<DMOMapPartnershipInviteListItem[]>(
      `companies/${selectedCompanyId.value}/maps/${mapId}/invites/`,
    )
    return data
  })
}

export function useDMOMapPartnershipInviteDetailsMutation(
  inviteUuid: Ref<string | null>,
) {
  return useMutation(async () => {
    const { data } = await axios.get<DMOMapPartnershipInviteDetails>(
      `public/map-invites/${inviteUuid.value}/`,
      {
        params: {},
      },
    )
    return data
  })
}

export function useDMOMapPartnershipInviteAcceptMutation(
  companyId: Ref<number | null>,
  inviteUuid: Ref<string | null>,
) {
  return useMutation(async () => {
    // Accept is company specific endpoint
    const { data } = await axios.put<DMOMapPartnershipInviteDetails>(
      `companies/${companyId.value}/maps/${inviteUuid.value}/accept/`,
    )
    return data
  })
}

export function useDMOMapPartnershipInviteRejectMutation(
  inviteUuid: Ref<string | null>,
) {
  return useMutation(async () => {
    // Unlike accept, rejecting is public endpoint
    const { data } = await axios.put<DMOMapPartnershipInviteDetails>(
      `public/map-invites/${inviteUuid.value}/reject/`,
    )
    return data
  })
}
